import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { CommonPageProps } from "../types";
import { CHEMICAL_MANAGEMENT_TYPE, HOLIDAY_BITS, MESSAGES } from "../constants/const";
import "./css/pageTransition.css";
import CommonPage from "../components/commonPage";
import { useApi } from "../logic/classApi";
import {
  showErrorMessage,
  showOrderCompletionMessage,
  showWarningMessage,
} from "../components/appPane";
import { useChemicalApi } from "../logic/chemicalApi";
import { useChemicalVolumeApi } from "../logic/chemicalVolumeApi";
import { displayDatetimeString, getInputDateString, getJstWeekday } from "../logic/dateUtils";
import { useProcessPlanApi } from "../logic/processPlanApi";
import { useOrderApi } from "../logic/orderApi";
import TransitionChart from "../components/transitionChart";
import { PAGE, usePermission } from "../logic/permission";
import Loading from "../components/loading";
import useCookie from "../logic/cookie";
import holidayJp from "@holiday-jp/holiday_jp";
import { convertStr } from "../logic/util";

/** グラフ用生データ */
interface ChartDatas {
  /** 日付ラベル */
  labels: string[];
  /** 実績値 */
  actualVolumes: number[];
  /** 予測値 */
  estimateVolumes: number[];
  /** 納入予定値 */
  plans: number[];
  /** 開封済み箱残量実績値 */
  actualOpenedBoxVolumes: number[];
  /** 開封済み箱残量予測値 */
  estimateOpenedBoxVolumes: number[];
  /** 実績値 */
  actualUsages: number[];
  /** 予測値 */
  estimateUsages: number[];
}

// 30分刻みの時刻選択肢を生成する（例: ["00:00", "00:30", "01:00", ...]）
const THIRTY_MIN_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

/**
 * 薬剤推移表示・発注画面
 * @param props
 * @returns
 */
const Transition: React.FC<CommonPageProps> = ({ showHeader, showSidebar }) => {
  // 表示権限チェック
  const [permission] = usePermission(PAGE.ORDER);
  useEffect(() => {
    if (!permission.canDisplay) window.close();
  }, []);
  if (!permission.canDisplay) {
    // 表示権限がない場合は、表示可能画面に自動遷移させるため、ローディング表示のみを返す
    return <Loading />;
  }
  // 支所コード、炉の数、不燃粗大施設稼働曜日（Cookieから取得）
  const [cookieData] = useCookie();
  const branchCode = cookieData.branchCode;
  const branchFurnaces = cookieData.branchFurnaces;
  const branchFunenWeek = JSON.parse(cookieData.branchFunenWeek) as number[];
  // 選択中の薬剤（初期値はパスパラメータから取得）
  const { chemicalId: initChemicalId } = useParams();
  const [chemicalId, setChemicalId] = useState<number>(Number(initChemicalId));
  // API呼び出し用hook
  const api = useApi();
  const [chemicalData] = useChemicalApi(api, branchCode);
  const [volumeData, volumeHandle] = useChemicalVolumeApi(api, branchCode);
  const [orderData, orderHandle] = useOrderApi(api, branchCode);
  const [processPlanData, processPlanHandle] = useProcessPlanApi(api, branchCode, branchFurnaces);
  // グラフ表示の開始日、終了日
  const centerDate = new Date();
  const startDate = new Date(centerDate);
  startDate.setDate(centerDate.getDate() - 14);
  const endDate = new Date(centerDate);
  endDate.setDate(centerDate.getDate() + 365);
  const orderEndDate = new Date(endDate);
  orderEndDate.setDate(orderEndDate.getDate() - 1);
  // グラフの表示用フラグ
  const [isShowVolumeChart, setShowVolumeChart] = useState(true);

  // 選択中の薬剤情報
  const chemical = useMemo(
    () => chemicalData.chemicals?.find(c => c.chemicalId === chemicalId),
    [chemicalId, chemicalData.isLoaded]
  );

  // 在庫上限 TODO: 管理用途ごとに参照カラムを切り替える
  const limit = useMemo(
    () => (chemical?.chemicalViewtype === 3 ? chemical?.chemicalLimit : chemical?.chemicalLimit),
    [chemical]
  );

  // 発注レベル TODO: 管理用途ごとに参照カラムを切り替える
  const border = useMemo(
    () =>
      chemical?.chemicalViewtype === 3 ? chemical?.chemicalBorder2 : chemical?.chemicalBorder2,
    [chemical]
  );

  // 選択中の薬剤でグラフ表示するかどうか
  const isShowChart = useMemo(() => chemical, [chemical]);

  // 選択中のベンダー情報
  const vendor = useMemo(
    () => chemicalData.vendors?.find(v => v.vendorId === chemical?.vendorId),
    [chemical?.vendorId, chemicalData.isLoaded]
  );

  // 発注業者メモ
  const [vendorMemo, setVendorMemo] = useState<string>(vendor?.vendorMemo ?? "なし");

  // 発注業者リードタイム
  const [vendorLeadTime, setVendorLeadTime] = useState<number>(vendor?.vendorLeadTime ?? 0);

  // 発注業者土日祝対応可能日
  const [vendorDeliverySupportDays, setVendorDeliverySupportDays] = useState<number>(
    vendor?.vendorDeliverySupportDays ?? 0
  );

  // 仮発注情報
  const [orderVolume, setOrderVolume] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [orderTime, setOrderTime] = useState("");
  const [orderTimeEnd, setOrderTimeEnd] = useState("");
  const [isTimeRange, setIsTimeRange] = useState(false);
  // 仮発注情報のグラフ反映有無
  const [isApplyPreOrder, setApplyPreOrder] = useState<boolean>(false);

  // グラフ表示対象日付のリスト
  const chartDates = useMemo(() => {
    const dates = [];
    const targetDate = new Date(startDate);
    for (let i = 0; i < 29; i++) {
      const date = new Date(targetDate);
      dates.push(date);
      targetDate.setDate(targetDate.getDate() + 1);
    }
    return dates;
  }, [startDate]);

  // グラフ用の生データ
  const chartDatas = useMemo((): ChartDatas => {
    // 表示対象日付リストからX軸に表示するラベルを生成
    const dates = chartDates;
    const labels = dates.map(d => displayDatetimeString(d.toISOString())[0]?.slice(-8));

    // 薬剤情報、在庫残量情報、発注情報、焼却量情報がそろってない間はグラフ表示不可なので空データを返す
    const emptyDatas = new Array(29).fill(0);
    if (
      !chemicalData.isLoaded ||
      !volumeData.volumes ||
      !orderData.orders ||
      !processPlanData.plans
    )
      return {
        labels,
        actualVolumes: emptyDatas,
        estimateVolumes: emptyDatas,
        plans: emptyDatas,
        actualOpenedBoxVolumes: emptyDatas,
        estimateOpenedBoxVolumes: emptyDatas,
        actualUsages: emptyDatas,
        estimateUsages: emptyDatas,
      };

    // 発注単位と表示単位の補正値
    const calc = chemical?.chemicalUnit2 ? (chemical?.chemicalUnit2Calc ?? 1) : 1;

    // グラフ表示対象期間の在庫残量データリスト
    const volumes = dates.map(d => volumeData.getVolume(chemicalId, d));
    // グラフ表示対象期間の在庫残量実績値リスト
    const actualVolumes = volumes.map(v => v?.volumeData ?? 0);

    // ※箱系管理の場合は残量の箱数から発注単位への変換を行い、開封済み箱残量も含める
    const calculatedVolumes =
      chemical?.chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.TANK
        ? volumes
        : volumes.map(v => {
            if (v) {
              v.volumeData = (v?.volumeData ?? 0) * calc + (v?.volumeOpenedBoxData ?? 0);
            }
            return v;
          });

    // グラフ表示対象期間の開封済み箱残量実績値リスト
    const actualOpenedBoxVolumes = volumes.map(v => v?.volumeOpenedBoxData ?? 0);

    // 在庫残量データが取れている最終日のインデックスを取得
    const actualLastIndex = volumes.map(v => !!v).lastIndexOf(true);
    // 在庫残量データが取れている最終日の次の日から予測値表示を開始
    const estimateStartIndex = actualLastIndex + 1;

    // 平均実績原単位を取得（存在しない場合は設計原単位を用いる）
    const actualIntensity = chemical?.chemicalIntensityAverage ?? chemical?.chemicalIntensity ?? 0;

    // 不燃粗大施設稼働曜日による使用量リストを作成
    const funenUsages = dates.map(d => {
      const dayOfWeek = d.getDay();
      return branchFunenWeek[dayOfWeek] ? (chemical?.chemicalCalc01 ?? 0) : 0;
    });

    // 焼却量計画値から、予測使用量のリストを作成
    const estimateUsages = dates.map((d, index) => {
      let usageValue = 0;
      // 対象日付が予測対象範囲か判定
      if (index >= actualLastIndex) {
        // 対象日付が共通停炉か判定
        if (processPlanData.getProcessPlanDataSum(d) > 0) {
          // 共通停炉していない場合は「焼却量×平均実績原単位」を使用量に加算
          usageValue += processPlanData.getProcessPlanDataSum(d) * actualIntensity;
        } else {
          // 共通停炉している場合は「(1)共通停炉時使用量」を使用量に加算
          usageValue += chemical?.chemicalCalc12 ? chemical.chemicalCalc12 : 0;
        }
        // 不燃粗大施設稼働時使用量を加算
        usageValue += funenUsages[index];
      }
      return usageValue;
    });
    // 最終確認されている在庫実績残量
    const lastVolume = calculatedVolumes[actualLastIndex]?.volumeData ?? 0;
    // グラフ表示対象期間の発注量のリスト（実績値があれば実績値を使用）
    const planVolumes = dates.map(d =>
      orderData
        .getOrdersByDate(chemicalId, d)
        .reduce((sum, o) => sum + (o.orderResultvolume ?? o.orderVolume ?? 0), 0)
    );
    // 使用量の実績値（翌日と当日の在庫実績値の差分が当日の使用量（ただし、発注による増分は除く））
    const actualUsages = dates.map((d, index) => {
      const current = calculatedVolumes[index];
      const next = calculatedVolumes[index + 1];
      const plan = planVolumes[index] / calc;
      if (current && next) {
        const usage = current.volumeData - (next.volumeData - plan);
        // 箱系の場合はすでに発注単位で補正済みなのでそのまま返す
        return chemical?.chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.TANK ? usage * calc : usage;
      }
      return 0;
    });
    // 在庫残量の予測値リストを生成（最終の在庫残量から当日までの予測使用量の合計を減算（発注予定による増分も加算））
    const rawEstimateVolumes = dates.map((_, index) =>
      index >= estimateStartIndex
        ? lastVolume -
          estimateUsages.slice(actualLastIndex, index).reduce((sum, u) => sum + u, 0) / calc +
          planVolumes.slice(estimateStartIndex, index).reduce((sum, v) => sum + v, 0) / calc
        : 0
    );

    // 箱系の場合は予測値の整数部分を箱残量、少数部分は開封済み箱残量とする
    const [estimateVolumes, estimateOpenedBoxVolumes] =
      chemical?.chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.TANK
        ? [rawEstimateVolumes, []]
        : rawEstimateVolumes.reduce(
            (results, rawEstimateVolume) => {
              const intVolume = Math.floor(rawEstimateVolume);
              const boxVolume = rawEstimateVolume - intVolume;
              results[0].push(intVolume);
              results[1].push(boxVolume);
              return results;
            },
            [[], []] as number[][]
          );

    // 仮発注量による在庫残量増加分のリスト
    let plans = emptyDatas;
    if (isApplyPreOrder) {
      const plannedIndex = dates.findIndex(d => {
        const [dateString] = displayDatetimeString(d.toISOString());
        const [orderDateString] = displayDatetimeString(new Date(orderDate).toISOString());
        return dateString === orderDateString;
      });
      // 在庫残量の予測値が負の値になっている場合は、仮発注量による在庫残量増加分から減算
      plans = rawEstimateVolumes.map((v, index) => {
        if (index <= plannedIndex) return 0;
        return Number(orderVolume) / calc + (v < 0 ? v : 0);
      });
    }

    return {
      labels,
      actualVolumes,
      estimateVolumes,
      plans,
      actualOpenedBoxVolumes,
      estimateOpenedBoxVolumes,
      actualUsages,
      estimateUsages,
    };
  }, [
    chemicalData.isLoaded,
    volumeData.volumes,
    orderData.orders,
    processPlanData.plans,
    chemicalId,
    isApplyPreOrder,
  ]);

  // 仮発注情報のクリア
  const clearPreOrderInfos = () => {
    setApplyPreOrder(false);
    setOrderVolume("");
    setOrderDate("");
    setOrderTime("");
    setOrderTimeEnd("");
    setIsTimeRange(false);
  };

  // 薬剤リストの選択時
  const handleChangeChemical = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setChemicalId(Number(event.target.value));
    clearPreOrderInfos();
    setShowVolumeChart(true);
  };

  // タブ変更時
  const handleTabClick = (showVolumeChart: boolean) => {
    setShowVolumeChart(showVolumeChart);
  };

  // グラフに反映ボタン
  const handleApplyPreOrder = () => {
    setApplyPreOrder(true);
    // 残量グラフ表示に切り替え
    setShowVolumeChart(true);
  };

  // グラフからリセットボタン
  const handleReset = () => {
    setApplyPreOrder(false);
    // 残量グラフ表示に切り替え
    setShowVolumeChart(true);
  };

  // セレクトボックスの表示値を判定する
  const getTimeSelectValue = (orderTime: string, orderTimeEnd: string, isTimeRange: boolean): string => {
    if (isTimeRange) return "時間帯指定";
    if (orderTime === "AM" || orderTime === "PM") return orderTime;
    return "指定なし";
  };

  // 発注情報登録
  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (e.nativeEvent["submitter"].id === "apply") {
      // グラフに反映ボタンでSubmitされた場合は登録処理せず終了
      handleApplyPreOrder();
      return;
    }

    if (orderDate === "" || orderVolume === "") {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }

    // 最低発注量を下回っていれば警告ポップアップを表示
    if ((chemical?.chemicalMinOrdervolume ?? 0) > Number(orderVolume)) {
      const result = await showWarningMessage(MESSAGES.WARNING_INPUT_MIN_AMMOUNT);
      if (!result) return;
    }

    // リードタイムを考慮した日にちより過去であれば警告ポップアップを表示
    const targetDate = new Date(orderDate);
    const limitDate = new Date();
    limitDate.setDate(limitDate.getDate() + Number(vendorLeadTime));

    // 誤判定を防ぐため、時刻を一致させる
    targetDate.setHours(0, 0, 0, 0);
    limitDate.setHours(0, 0, 0, 0);

    if (targetDate < limitDate) {
      const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DATE);
      if (!result) return;
    }

    // 搬入対応可能日でなければ警告ポップアップを表示
    const day = targetDate.getDay();
    if (vendorDeliverySupportDays !== 0) {
      // 土曜日の場合
      if ((vendorDeliverySupportDays & HOLIDAY_BITS.SATURDAY) !== 0 && day === 6) {
        const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DAY);
        if (!result) return;
        // 日曜日の場合
      } else if ((vendorDeliverySupportDays & HOLIDAY_BITS.SUNDAY) !== 0 && day === 0) {
        const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DAY);
        if (!result) return;
        // 祝日の場合
      } else if (
        (vendorDeliverySupportDays & HOLIDAY_BITS.HOLIDAY) !== 0 &&
        holidayJp.isHoliday(targetDate)
      ) {
        const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DAY);
        if (!result) return;
      }
    }

    try {
      const selectValue = getTimeSelectValue(orderTime, orderTimeEnd, isTimeRange);
      const resolvedOrderTime =
        selectValue === "AM" ? "00:00" :
        selectValue === "PM" ? "12:01" :
        selectValue === "指定なし" ? "00:00" :
        orderTime;
      const resolvedOrderTimeEnd =
        selectValue === "AM" ? "12:00" :
        selectValue === "PM" ? "23:59" :
        selectValue === "指定なし" ? "00:00" :
        orderTimeEnd;

      await orderHandle.saveOrder(chemicalId, orderDate, resolvedOrderTime, resolvedOrderTimeEnd, Number(orderVolume));
      const selectButtonResult = await showOrderCompletionMessage(MESSAGES.SUCCESS_POST);
      if (selectButtonResult) {
        // 親画面を遷移
        opener.location.href = "/orderlist";
        // 子画面を閉じる
        window.close();
      } else {
        window.close();
      }
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_POST);
    }
  };

  // 画面初期表示時
  useEffect(() => {
    processPlanHandle.loadPlans(startDate, endDate);
  }, []);

  // 薬剤ID変更時
  useEffect(() => {
    volumeHandle.loadVolumes(chemicalId, startDate, endDate);
    orderHandle.loadOrdersByDeliveryDate(chemicalId, startDate, endDate);
  }, [chemicalId]);

  useEffect(() => {
    setOrderVolume(chemical?.chemicalMinOrdervolume?.toString() ?? "");

    setVendorMemo(vendor?.vendorMemo ?? "");
    setVendorLeadTime(vendor?.vendorLeadTime ?? 0);
    setVendorDeliverySupportDays(vendor?.vendorDeliverySupportDays ?? 0);
  }, [chemical]);

  useEffect(() => {
    if (vendorLeadTime !== undefined && vendorLeadTime !== null) {
      const today = new Date();
      const leadTimeDate = new Date(today);
      leadTimeDate.setDate(today.getDate() + Number(vendorLeadTime));
      setOrderDate(convertStr(leadTimeDate, "YYYY-MM-DD") ?? "");
    }
  }, [chemicalId, vendorLeadTime]);

  return (
    <CommonPage showHeader={showHeader} showSidebar={showSidebar}>
      <div className="app-main">
        {chemicalData.isLoaded && chemical && (
          <>
            <section className="chart-section">
              <div className="parts">
                <label>薬剤:</label>
                <select value={chemicalId} onChange={handleChangeChemical}>
                  {chemicalData.getValidChemicals().map(list => (
                    <option key={list.chemicalId} value={list.chemicalId}>
                      {list.chemicalName}
                    </option>
                  ))}
                </select>
              </div>

              {isShowChart && (
                <div className="tab">
                  <button
                    className={isShowVolumeChart ? "select" : ""}
                    onClick={() => {
                      handleTabClick(true);
                    }}
                  >
                    残量
                  </button>
                  <button
                    className={!isShowVolumeChart ? "select" : ""}
                    onClick={() => {
                      handleTabClick(false);
                    }}
                  >
                    使用量
                  </button>
                </div>
              )}

              <div className="chart">
                {isShowChart && (
                  <>
                    {isShowVolumeChart ? (
                      <TransitionChart
                        labels={chartDatas.labels}
                        actuals={chartDatas.actualVolumes}
                        estimates={chartDatas.estimateVolumes}
                        plans={chartDatas.plans}
                        actualOpenedBoxVolumes={
                          chemical.chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.TANK
                            ? undefined
                            : chartDatas.actualOpenedBoxVolumes
                        }
                        estimateOpenedBoxVolumes={
                          chemical.chemicalViewtype === CHEMICAL_MANAGEMENT_TYPE.TANK
                            ? undefined
                            : chartDatas.estimateOpenedBoxVolumes
                        }
                        title={"残量推移"}
                        max={chemical.chemicalLimit}
                        limit={limit}
                        border={border}
                        unit={chemical.chemicalUnit1}
                      />
                    ) : (
                      <TransitionChart
                        labels={chartDatas.labels}
                        actuals={chartDatas.actualUsages}
                        estimates={chartDatas.estimateUsages}
                        title={"使用量"}
                        unit={chemical.chemicalUnit2 || chemical.chemicalUnit1}
                      />
                    )}
                  </>
                )}
              </div>
            </section>

            <section className="order-section">
              {isShowVolumeChart && permission.canEdit && (
                <>
                  <h4>発注登録</h4>
                  <form onSubmit={sendForm}>
                    <div className="parts">
                      <label>納入数量({chemical.chemicalUnit2 || chemical.chemicalUnit1})：</label>
                      <input
                        type="number"
                        id="additional-value"
                        value={orderVolume}
                        step={"any"}
                        min={0}
                        onChange={e => setOrderVolume(e.target.value)}
                        required
                        disabled={isApplyPreOrder}
                      />
                      {chemical.chemicalUnit2 !== "" && (chemical.chemicalUnit2Calc ?? 0) > 0 && (
                        <div>
                          ※1{chemical.chemicalUnit1}あたり{chemical.chemicalUnit2Calc}
                          {chemical.chemicalUnit2}
                        </div>
                      )}
                    </div>
                    <div className="parts">
                      <label>納入予定日：</label>
                      <input
                        type="date"
                        value={orderDate}
                        min={getInputDateString(centerDate)}
                        max={getInputDateString(orderEndDate)}
                        onChange={e => setOrderDate(e.target.value)}
                        required
                        disabled={isApplyPreOrder}
                      ></input>
                      {isShowChart && (
                        <div className="parts actions apply">
                          <button id="apply" type="submit" className="positive">
                            グラフに反映
                          </button>
                          <button type="button" onClick={handleReset} className="negative">
                            グラフからリセット
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="parts">
                      <label>納入予定時刻：</label>
                      <select
                        className="grid-cell-item"
                        value={getTimeSelectValue(orderTime, orderTimeEnd, isTimeRange)}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === "指定なし") {
                            setOrderTime("");
                            setOrderTimeEnd("");
                            setIsTimeRange(false);
                          } else if (val === "AM" || val === "PM") {
                            setOrderTime(val);
                            setOrderTimeEnd("");
                            setIsTimeRange(false);
                          } else {
                            setOrderTime("");
                            setOrderTimeEnd("");
                            setIsTimeRange(true);
                          }
                        }}
                      >
                        <option>指定なし</option>
                        <option>AM</option>
                        <option>PM</option>
                        <option>時間帯指定</option>
                      </select>
                      {getTimeSelectValue(orderTime, orderTimeEnd, isTimeRange) === "時間帯指定" && (
                        <>
                          <select
                            className="grid-cell-item"
                            value={orderTime}
                            onChange={e => {
                              setOrderTime(e.target.value);
                              // 開始時刻変更時、終了時刻が開始時刻より前になる場合はリセット
                              if (orderTimeEnd && orderTimeEnd < e.target.value) {
                                setOrderTimeEnd("");
                              }
                            }}
                          >
                            {THIRTY_MIN_OPTIONS.map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <span>〜</span>
                          <select
                            className="grid-cell-item"
                            value={orderTimeEnd}
                            onChange={e => setOrderTimeEnd(e.target.value)}
                          >
                            {/* 終了時刻は開始時刻以降の選択肢のみ表示 */}
                            {THIRTY_MIN_OPTIONS.filter(time => !orderTime || time >= orderTime).map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>

                    <div className="parts">
                      <label>メモ：</label>
                      <div style={{ width: "50ch" }}>{vendorMemo}</div>
                    </div>

                    <div className="parts actions">
                      <button type="submit" className="positive" id="register">
                        上記内容で発注登録
                      </button>
                    </div>
                  </form>
                </>
              )}
            </section>
          </>
        )}
        {chemicalData.isLoaded && !chemical && <div>不正な薬剤、もしくは存在しない薬剤です</div>}
      </div>
    </CommonPage>
  );
};

export default Transition;
